import { RouteObject } from "react-router-dom";
import wretch from "wretch";
import Bugsnag from "@bugsnag/js";
import { DataService } from "@/services/data";
import { SireneSearchResult } from "@/types/utils/sirene";
import { bugsnagNotify } from "@/utils/bugsnag-notify";
import { handle_error, handle_success } from "@/utils/wretch";
import { WretchError } from "wretch/resolver";

const UtilsRoute: RouteObject = {
    path: "utils",
    children: [
        {
            path: "reset",
            loader: async () => {
                return null;
            }
        },
        {
            path: "address/search",
            loader: async ({ request }) => {
                try {
                    const url = new URL(request.url);
                    const data = (await wretch("https://api-adresse.data.gouv.fr").url(`/search/?q=${url.searchParams.toString()}&type=housenumber`).get().json()) as {
                        features: {
                            properties: {
                                label: string;
                                housenumber: string;
                                street: string;
                                postcode: string;
                                city: string;
                            };
                            geometry: { coordinates: number[] };
                        }[];
                    };
                    const cities = data.features.map(f => {
                        return {
                            label: f.properties.label,
                            housenumber: f.properties.housenumber,
                            street: f.properties.street,
                            postcode: f.properties.postcode,
                            city: f.properties.city,
                            country: "France",
                            longitude: f?.geometry?.coordinates?.[0],
                            latitude: f.geometry?.coordinates?.[1]
                        };
                    });
                    if (cities.some(c => !c.longitude || !c.latitude)) {
                        Bugsnag.notify(`Missing longitude or latitude in address/search: ${JSON.stringify(cities)}`);
                    }
                    return cities;
                } catch (e) {
                    if (e instanceof WretchError) {
                        if (e.status !== 504) {
                            bugsnagNotify("UtilsRoute > loader > address/search", {}, e)
                        }
                    }
                    console.error(e);
                    return [];
                }
            }
        },
        {
            path: "address/cities/:postcode",
            loader: async ({ params }) => {
                try {
                    const data = (await wretch("https://api-adresse.data.gouv.fr").url(`/search/?q=${params.postcode}&postcode=${params.postcode}&type=municipality`).get().json()) as {
                        features: {
                            properties: {
                                postcode: string;
                                name: string;
                                label: string;
                            };
                        }[];
                    };
                    const v1 = data.features.filter(f => f.properties.postcode == params.postcode);
                    const v2 = data.features.filter(f => params.postcode && f.properties.postcode.slice(0, 2) == params.postcode.slice(0, 2));
                    const _features = v1.length > 0 ? v1 : v2;
                    const cities = _features.map(f => {
                        return { value: f.properties?.name, label: f.properties?.label };
                    });
                    return cities;
                } catch (e) {
                    console.error(e);
                    return [];
                }
            }
        },
        {
            path: "search_opco/:siret",
            loader: async ({ params }) => {
                try {
                    const data = (await DataService.client.url(`/registration/utils/search_opco?siret=${params.siret}`).get().json()) as {
                        opco_name: string | null;
                        opco_code: string | null;
                    };
                    return data;
                } catch (e) {
                    console.error(e);
                    return null;
                }
            }
        },
        {
            path: "siret/search",
            loader: async ({ request }) => {
                try {
                    const url = new URL(request.url);
                    let customQueryParameters = [];
                    // Extract the search query
                    const q = url.searchParams.get("q");
                    url.searchParams.delete("q");
                    if (q) {
                        customQueryParameters.push(
                            `where=search(denominationunitelegale%2C%20%22${q}%22) OR search(denominationusuelleetablissement%2C%20%22${q}%22) OR search(l1_adressage_unitelegale%2C%20%22${q}%22)`
                        );
                    }
                    const data = (await wretch("https://public.opendatasoft.com")
                        .url(`/api/explore/v2.1/catalog/datasets/economicref-france-sirene-v3/records?${customQueryParameters.join("&")}&${url.searchParams.toString()}`)
                        .get()
                        .json()) as {
                        total_count: number;
                        results: SireneSearchResult[];
                    };
                    return data;
                } catch (e) {
                    console.error(e);
                    return {
                        total_count: 0,
                        results: []
                    };
                }
            }
        },
        {
            path: "siret/:siret",
            loader: async ({ params }) => {
                try {
                    const data = (await wretch("https://public.opendatasoft.com")
                        .url(`/api/explore/v2.1/catalog/datasets/economicref-france-sirene-v3/records?where=siret%20%3D%20'${params.siret!.replace(/\D/g, "")}'&limit=20`)
                        .get()
                        .json()) as {
                        total_count: number;
                        results: SireneSearchResult[];
                    };
                    return data.results;
                } catch (e) {
                    console.error(e);
                    return [];
                }
            }
        },
        {
            path: "activity/track",
            action: async ({ request }) => {
                try {
                    const payload = await request.json();
                    const tracked = await DataService.client.url("/users/activities/track/").post(payload).json();
                    return handle_success(tracked);
                } catch (error) {
                    return handle_error(error);
                }
            }
        }
    ]
};
export default UtilsRoute;
